/**
 * Shared tier metadata for AI model rows.
 *
 * Used by both the Copilot chat sidebar model picker (preference-popup.ts)
 * and the workspace AI settings panel (tier-model-panel.tsx) so the two
 * surfaces render identical tier pills.
 */

export type ModelTier = 'pro' | 'standard' | 'economy';

export const TIER_ORDER: readonly ModelTier[] = ['pro', 'standard', 'economy'];

export interface TierPillStyle {
  label: string;
  bg: string;
  fg: string;
}

export const TIER_CONFIG: Record<ModelTier, TierPillStyle> = {
  pro: { label: 'Pro', bg: '#f3e8ff', fg: '#7c3aed' },
  standard: { label: 'Standard', bg: '#dbeafe', fg: '#1d4ed8' },
  economy: { label: 'Economy', bg: '#dcfce7', fg: '#15803d' },
};

export function normalizeModelTier(tier: ModelTier | string): ModelTier {
  if (tier === 'professional' || tier === 'pro') {
    return 'pro';
  }
  if (tier === 'economy') return 'economy';
  return 'standard';
}

/** Inline CSS style string for a tier pill (Lit `style` attribute). */
export function tierPillStyleString(tier: ModelTier | string): string {
  const cfg = TIER_CONFIG[normalizeModelTier(tier)];
  return `font-size:10px;font-weight:500;padding:1px 5px;border-radius:10px;background:${cfg.bg};color:${cfg.fg};white-space:nowrap;min-width:56px;text-align:center`;
}

/** React CSSProperties object for a tier pill. */
export function tierPillStyle(tier: ModelTier | string): React.CSSProperties {
  const cfg = TIER_CONFIG[normalizeModelTier(tier)];
  return {
    fontSize: 10,
    fontWeight: 500,
    padding: '1px 5px',
    borderRadius: 10,
    background: cfg.bg,
    color: cfg.fg,
    whiteSpace: 'nowrap',
    minWidth: 56,
    textAlign: 'center',
  };
}

export function tierLabel(tier: ModelTier | string): string {
  return TIER_CONFIG[normalizeModelTier(tier)].label;
}

export function sortModelsByTierAndCost<
  T extends { tier: string; name: string },
>(models: readonly T[], getCostMultiplier: (model: T) => number): T[] {
  const tierRank = new Map<string, number>(
    TIER_ORDER.map((tier, index) => [tier, index] as const)
  );
  const rank = (tier: string) =>
    tierRank.get(normalizeModelTier(tier)) ?? TIER_ORDER.length;

  return [...models].sort((a, b) => {
    const tierDiff = rank(a.tier) - rank(b.tier);
    if (tierDiff !== 0) return tierDiff;

    const costDiff = getCostMultiplier(b) - getCostMultiplier(a);
    if (costDiff !== 0) return costDiff;

    return b.name.localeCompare(a.name);
  });
}
